import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';

interface Project {
  _id: string;
  title: string;
  status: string;
  template: string;
  progress: {
    totalTasks: number;
    completedTasks: number;
    currentTask: string;
  };
  totalCostUsd: number;
  createdAt: string;
  completedAt?: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  queued: { label: 'Queued', color: 'bg-gray-100 text-gray-700' },
  scaffolding: { label: 'Scaffolding', color: 'bg-blue-100 text-blue-700' },
  developing: { label: 'Developing', color: 'bg-yellow-100 text-yellow-700' },
  testing: { label: 'Testing', color: 'bg-purple-100 text-purple-700' },
  fixing: { label: 'Fixing', color: 'bg-orange-100 text-orange-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
};

const DEV_ENGINE_URL = '/dev-engine';

export default function ProjectsPage() {
  const queryClient = useQueryClient();

  const { data: projects = [], isLoading } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await apiFetch(`${DEV_ENGINE_URL}/projects`);
      if (!res.ok) throw new Error('Failed to fetch projects');
      return res.json();
    },
    refetchInterval: 5000, // ポーリング（アクティブプロジェクトがある場合）
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`${DEV_ENGINE_URL}/projects/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <h1 className="text-3xl font-bold">Projects</h1>

        {projects.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center">
            <p className="text-lg text-muted-foreground">No projects yet</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Start auto-development from a blueprint to create a project
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => {
              const statusInfo = STATUS_LABELS[project.status] || STATUS_LABELS.queued;
              const progressPct = project.progress.totalTasks > 0
                ? Math.round((project.progress.completedTasks / project.progress.totalTasks) * 100)
                : 0;

              return (
                <div key={project._id} className="rounded-lg border bg-card p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <Link to={`/projects/${project._id}`} className="text-lg font-semibold hover:underline">
                        {project.title}
                      </Link>
                      <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <span>{project.template}</span>
                        <span>${(project.totalCostUsd || 0).toFixed(4)}</span>
                        <span>{new Date(project.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/projects/${project._id}`}>
                        <Button size="sm" variant="outline">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm('このプロジェクトを削除しますか？')) {
                            deleteMutation.mutate(project._id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {project.progress.totalTasks > 0 && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{project.progress.currentTask || '待機中'}</span>
                        <span>{project.progress.completedTasks}/{project.progress.totalTasks} ({progressPct}%)</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            project.status === 'completed' ? 'bg-green-500'
                            : project.status === 'failed' ? 'bg-red-500'
                            : 'bg-blue-500'
                          }`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
